import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { SvgIconComponent } from '@mui/icons-material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import EventNoteRounded from '@mui/icons-material/EventNoteRounded'
import RecordVoiceOverRounded from '@mui/icons-material/RecordVoiceOverRounded'
import FolderRounded from '@mui/icons-material/FolderRounded'
import HandshakeRounded from '@mui/icons-material/HandshakeRounded'
import PeopleRounded from '@mui/icons-material/PeopleRounded'
import HowToRegRounded from '@mui/icons-material/HowToRegRounded'
import FactCheckRounded from '@mui/icons-material/FactCheckRounded'
import RestaurantRounded from '@mui/icons-material/RestaurantRounded'
import FilterAltRounded from '@mui/icons-material/FilterAltRounded'
import DescriptionRounded from '@mui/icons-material/DescriptionRounded'
import ForumRounded from '@mui/icons-material/ForumRounded'
import CampaignRounded from '@mui/icons-material/CampaignRounded'
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded'
import WebRounded from '@mui/icons-material/WebRounded'
import EmailRounded from '@mui/icons-material/EmailRounded'
import SendRounded from '@mui/icons-material/SendRounded'
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded'
import HubRounded from '@mui/icons-material/HubRounded'
import HistoryRounded from '@mui/icons-material/HistoryRounded'
import { useNavigate, useParams } from 'react-router-dom'
import { adminService } from '../../services/adminService'
import type { EventInfo } from '../../data/types'
import { LoadingState } from '../../components/ui/States'
import { formatDateRange } from '../../utils/format'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import SpeakersManager from '../managers/SpeakersManager'
import SessionsManager from '../managers/SessionsManager'
import ContentManager from '../managers/ContentManager'
import FormsManager from '../managers/FormsManager'
import AttendeesManager from '../managers/AttendeesManager'
import TargetingManager from '../managers/TargetingManager'
import AuditManager from '../managers/AuditManager'
import NotificationsManager from '../managers/NotificationsManager'
import RegistrationFieldsManager from '../managers/RegistrationFieldsManager'
import RegistrationApprovalsManager from '../managers/RegistrationApprovalsManager'
import DinnersManager from '../managers/DinnersManager'
import PagesManager from '../managers/PagesManager'
import EmailComposerManager from '../managers/EmailComposerManager'
import EngagementManager from '../managers/EngagementManager'
import SponsorsManager from '../managers/SponsorsManager'
import CheckinManager from '../managers/CheckinManager'
import IntegrationsManager from '../managers/IntegrationsManager'
import OutreachManager from '../managers/OutreachManager'
import ConciergeManager from '../managers/ConciergeManager'
import { ExportEventDialog, ImportEventDialog } from '../components/EventBundleDialogs'

// Sections keep their original numeric index (the `tab` state) so the content
// switch below is unchanged; the sidebar is just a grouped view over them.
type Section = { index: number; label: string; icon: SvgIconComponent }
type SectionGroup = { heading: string; items: Section[] }

const SECTION_GROUPS: SectionGroup[] = [
  {
    heading: 'Program',
    items: [
      { index: 0, label: 'Agenda', icon: EventNoteRounded },
      { index: 1, label: 'Speakers', icon: RecordVoiceOverRounded },
      { index: 2, label: 'Content', icon: FolderRounded },
      { index: 11, label: 'Sponsors', icon: HandshakeRounded },
    ],
  },
  {
    heading: 'Audience',
    items: [
      { index: 4, label: 'Attendees', icon: PeopleRounded },
      { index: 7, label: 'Registration', icon: HowToRegRounded },
      { index: 18, label: 'Approvals', icon: FactCheckRounded },
      { index: 8, label: 'Dinners', icon: RestaurantRounded },
      { index: 5, label: 'Targeting', icon: FilterAltRounded },
    ],
  },
  {
    heading: 'Engagement',
    items: [
      { index: 3, label: 'Forms', icon: DescriptionRounded },
      { index: 12, label: 'Engagement', icon: ForumRounded },
      { index: 6, label: 'Notify', icon: CampaignRounded },
      { index: 16, label: 'Concierge', icon: AutoAwesomeRounded },
    ],
  },
  {
    heading: 'Site & Comms',
    items: [
      { index: 9, label: 'Pages', icon: WebRounded },
      { index: 10, label: 'Emails', icon: EmailRounded },
      { index: 14, label: 'Outreach', icon: SendRounded },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { index: 13, label: 'Check-in', icon: QrCodeScannerRounded },
      { index: 15, label: 'Integrations', icon: HubRounded },
      { index: 17, label: 'Activity', icon: HistoryRounded },
    ],
  },
]

const ALL_SECTIONS: Section[] = SECTION_GROUPS.flatMap((g) => g.items)

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  published: { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
  draft: { color: '#B5611A', bg: 'rgba(228,119,32,0.15)' },
  archived: { color: '#707070', bg: 'rgba(112,112,112,0.12)' },
}

export default function AdminEventDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { notify } = useToast()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [event, setEvent] = useState<EventInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState(0)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [templateDialog, setTemplateDialog] = useState(false)
  const [bundleDialog, setBundleDialog] = useState<'export' | 'import' | null>(null)

  const load = useCallback(() => {
    adminService
      .getEvent(id)
      .then(setEvent)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
  }, [id, notify])

  useEffect(() => {
    load()
  }, [load])

  const togglePublish = async () => {
    if (!event) return
    setMenuAnchor(null)
    try {
      const updated =
        (event.status ?? 'draft') === 'published'
          ? await adminService.unpublishEvent(id)
          : await adminService.publishEvent(id)
      setEvent(updated)
      notify(updated.status === 'published' ? 'Event published' : 'Event unpublished')
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const clone = async () => {
    setMenuAnchor(null)
    try {
      const created = await adminService.cloneEvent(id)
      notify('Event cloned')
      navigate(`/admin/events/${created.id}`)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const openTemplateDialog = () => {
    setMenuAnchor(null)
    setTemplateDialog(true)
  }

  const remove = async () => {
    setMenuAnchor(null)
    if (!(await confirmDelete(`Delete event "${event?.name}"? This cannot be undone.`))) return
    try {
      await adminService.deleteEvent(id)
      notify('Event deleted')
      navigate('/admin')
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading || !event) return <LoadingState />

  const status = event.status ?? 'draft'
  const sc = STATUS_COLORS[status] ?? STATUS_COLORS.draft

  const activeLabel =
    ALL_SECTIONS.find((s) => s.index === tab)?.label ?? 'Section'

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate('/admin')}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        All events
      </Button>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        justifyContent="space-between"
        spacing={{ xs: 2, md: 0 }}
        sx={{ mb: 1 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="h4" fontWeight={700}>
              {event.name}
            </Typography>
            <Chip
              label={status}
              size="small"
              sx={{
                fontWeight: 700,
                textTransform: 'capitalize',
                color: sc.color,
                bgcolor: sc.bg,
              }}
            />
          </Stack>
          <Typography variant="body1" color="text.secondary">
            {formatDateRange(event.startsAt, event.endsAt)}
            {event.location ? ` · ${event.location}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            startIcon={<InsightsRoundedIcon />}
            onClick={() => navigate(`/admin/events/${id}/reports`)}
          >
            Reports
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditRoundedIcon />}
            onClick={() => navigate(`/admin/events/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            endIcon={<MoreVertRoundedIcon />}
          >
            Actions
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {event.slug && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  window.open(`/e/${event.slug}`, '_blank', 'noopener')
                }}
              >
                View attendee site
              </MenuItem>
            )}
            <MenuItem onClick={togglePublish}>
              {status === 'published' ? 'Unpublish' : 'Publish'}
            </MenuItem>
            <MenuItem onClick={clone}>Duplicate event</MenuItem>
            <MenuItem onClick={openTemplateDialog}>Save as template</MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                setBundleDialog('export')
              }}
            >
              Export event…
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null)
                setBundleDialog('import')
              }}
            >
              Import event…
            </MenuItem>
            <Divider />
            <MenuItem onClick={remove} sx={{ color: 'error.main' }}>
              Delete event
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* Grouped left sidebar (desktop) + content pane. On mobile the sidebar
          collapses to a Select so the page never needs a wide viewport. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 3 }}
        alignItems="flex-start"
        sx={{ mt: 2, mb: 3 }}
      >
        {isMobile ? (
          <Select
            size="small"
            fullWidth
            value={tab}
            onChange={(e) => setTab(Number(e.target.value))}
          >
            {SECTION_GROUPS.flatMap((group) => [
              <MenuItem
                key={`h-${group.heading}`}
                disabled
                sx={{ fontWeight: 700, opacity: 1, color: 'text.secondary' }}
              >
                {group.heading}
              </MenuItem>,
              ...group.items.map((s) => (
                <MenuItem key={s.index} value={s.index} sx={{ pl: 3 }}>
                  {s.label}
                </MenuItem>
              )),
            ])}
          </Select>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              width: 220,
              flexShrink: 0,
              borderRadius: 2,
              overflow: 'hidden',
              position: 'sticky',
              top: 16,
            }}
          >
            {SECTION_GROUPS.map((group) => (
              <Box key={group.heading}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    px: 2,
                    pt: 1.5,
                    pb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 700,
                    lineHeight: 1.6,
                  }}
                >
                  {group.heading}
                </Typography>
                <List dense disablePadding>
                  {group.items.map((s) => {
                    const active = tab === s.index
                    const Icon = s.icon
                    return (
                      <ListItemButton
                        key={s.index}
                        selected={active}
                        onClick={() => setTab(s.index)}
                        sx={{
                          py: 0.6,
                          borderLeft: '3px solid',
                          borderLeftColor: active ? 'primary.main' : 'transparent',
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': { bgcolor: 'action.selected' },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                          <Icon
                            fontSize="small"
                            color={active ? 'primary' : 'inherit'}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={s.label}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: active ? 700 : 500,
                            color: active ? 'primary.main' : 'text.primary',
                          }}
                        />
                      </ListItemButton>
                    )
                  })}
                </List>
              </Box>
            ))}
          </Paper>
        )}

        {/* Content pane */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            width: { xs: '100%', md: 'auto' },
            borderRadius: 2,
            bgcolor: 'background.paper',
            p: { xs: 2, md: 3 },
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {activeLabel}
          </Typography>
      {tab === 0 && <SessionsManager eventId={id} />}
      {tab === 1 && <SpeakersManager eventId={id} />}
      {tab === 2 && <ContentManager eventId={id} />}
      {tab === 3 && <FormsManager eventId={id} />}
      {tab === 4 && <AttendeesManager eventId={id} />}
      {tab === 5 && <TargetingManager eventId={id} />}
      {tab === 6 && <NotificationsManager eventId={id} />}
      {tab === 7 && <RegistrationFieldsManager eventId={id} />}
      {tab === 18 && <RegistrationApprovalsManager eventId={id} />}
      {tab === 8 && <DinnersManager eventId={id} />}
      {tab === 9 && <PagesManager eventId={id} />}
      {tab === 10 && <EmailComposerManager eventId={id} />}
      {tab === 11 && <SponsorsManager eventId={id} />}
      {tab === 12 && <EngagementManager eventId={id} />}
      {tab === 13 && <CheckinManager eventId={id} />}
      {tab === 14 && <OutreachManager eventId={id} />}
      {tab === 15 && <IntegrationsManager eventId={id} />}
      {tab === 16 && <ConciergeManager eventId={id} />}
      {tab === 17 && <AuditManager eventId={id} />}
        </Paper>
      </Stack>

      {templateDialog && event && (
        <SaveAsTemplateDialog
          eventId={id}
          defaultName={`${event.name} Template`}
          onClose={() => setTemplateDialog(false)}
        />
      )}
      {bundleDialog === 'export' && event && (
        <ExportEventDialog
          eventId={id}
          eventName={event.name}
          onClose={() => setBundleDialog(null)}
        />
      )}
      {bundleDialog === 'import' && <ImportEventDialog onClose={() => setBundleDialog(null)} />}
    </Container>
  )
}

function SaveAsTemplateDialog({
  eventId,
  defaultName,
  onClose,
}: {
  eventId: string
  defaultName: string
  onClose: () => void
}) {
  const { notify } = useToast()
  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!name.trim()) {
      notify('Template name is required', 'error')
      return
    }
    setSaving(true)
    try {
      await adminService.saveAsTemplate(eventId, {
        name: name.trim(),
        description: description.trim() || undefined,
      })
      notify('Saved as template')
      onClose()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save as template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Save this event's structure (agenda, content, forms, branding) as a
            reusable template. Attendees and responses are not copied.
          </Typography>
          <TextField
            label="Template name"
            required
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            placeholder="When to use this template"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save template'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
